import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';

import { Form, Input, Message } from '@arco-design/web-react';

import type { CommonImageItemType } from '@/components-biz/ImageSelect';

import { PluginModal } from '../../components/PluginModal';
import { buildDiskPricingInitPatch } from '../../components/diskPricingSync';
import { DEFAULT_DISK_SIZE, DISK_SIZE_MARK_COUNT, DiskVolumeFields, readDiskVolumeFormValues } from '../../components/diskVolumeFields';
import { ImageResourceSelectField } from '../../components/imageResourceSelect';
import { isReclaimPolicyLocked, resolveReclaimPolicyValue } from '../../components/reclaimPolicyField';
import { normalizeVendorDeviceMeta, readDiskPricingMeta } from '../../components/vendorDevice';
import { OptionMetaRootField } from '../../platform/option-meta';
import { getModalPopupContainer } from '../../utils/modalPopupContainer';
import { parseSystemVolumeSizeGi } from '../system-disk-size';
import { SYSTEM_DISK_NAME } from './constants';
import styles from './index.module.less';
import type { ImageVmCreateModalProps, SystemDiskImageInfo } from './types';
import { mapCommonImageToSystemDiskImage } from './utils';

enum Field {
  DISK_SIZE = 'diskSize',
  STORAGE_CLASS = 'storageClass',
  RECLAIM_POLICY = 'reclaimPolicy',
  IMAGE_ID = 'imageId',
  IMAGE = 'image',
  IMAGE_DISPLAY = '_imageDisplayName',
}

/** 新建 / 编辑系统盘弹窗 */
export function ImageVmCreateModal({
  visible,
  form,
  title,
  initDisk,
  imageQuery,
  onOk,
  onCancel,
  onImageItemChange,
  billModelId,
  deploymentTypeId,
  paymentTiming,
  skuType,
  resourceEditMode,
  resourceCode,
}: ImageVmCreateModalProps) {
  const { t } = useTranslation();
  const [selectedImage, setSelectedImage] = useState<SystemDiskImageInfo>();
  const openInitializedRef = useRef(false);
  const watchedImageId = Form.useWatch(Field.IMAGE_ID, form) as string | undefined;
  const watchedImage = Form.useWatch(Field.IMAGE, form) as SystemDiskImageInfo | undefined;
  /** 自定义规格下单编辑已有盘：镜像 / 选型只读，仅改容量 */
  const selectionReadOnly = Boolean(initDisk) && resourceEditMode === 'order-custom';
  /** SKU / 下单：隐藏回收策略并固定 Delete */
  const reclaimLocked = isReclaimPolicyLocked(resourceEditMode);

  useEffect(() => {
    if (!visible) {
      openInitializedRef.current = false;
      setSelectedImage(undefined);
      onImageItemChange?.(undefined);
      return;
    }
    if (openInitializedRef.current) return;
    openInitializedRef.current = true;

    form.resetFields();
    if (initDisk) {
      form.setFieldsValue({
        [Field.DISK_SIZE]: Number(initDisk.diskSize) || DEFAULT_DISK_SIZE,
        [Field.STORAGE_CLASS]: initDisk.storageClass,
        [Field.RECLAIM_POLICY]: resolveReclaimPolicyValue(initDisk.reclaimPolicy, reclaimLocked),
        [Field.IMAGE_ID]: initDisk.imageId,
        [Field.IMAGE_DISPLAY]: initDisk.image?.name,
        ...buildDiskPricingInitPatch(initDisk.pricing),
      });
      setSelectedImage(initDisk.image);
    } else {
      form.setFieldsValue({
        [Field.DISK_SIZE]: DEFAULT_DISK_SIZE,
        [Field.RECLAIM_POLICY]: resolveReclaimPolicyValue(undefined, reclaimLocked),
      });
    }
  }, [form, initDisk, onImageItemChange, reclaimLocked, visible]);

  const handleImageSelected = useCallback(
    async (_key: string, item: CommonImageItemType, imageUrl?: string) => {
      const mapped = mapCommonImageToSystemDiskImage(item, imageUrl);
      setSelectedImage(mapped);
      onImageItemChange?.(item);

      /** 与 CI system-disk-size 同源：镜像推荐容量抬高盘大小下限 */
      const systemVolumeSize = parseSystemVolumeSizeGi(item, 0);
      const userInputSize = Number(form.getFieldValue(Field.DISK_SIZE)) || 0;
      if (systemVolumeSize > 0 && systemVolumeSize > userInputSize) {
        form.setFieldValue(Field.DISK_SIZE, systemVolumeSize);
      }
      form.setFieldValue(Field.IMAGE, mapped);
      form.setFieldValue(Field.IMAGE_DISPLAY, item.name);
    },
    [form, onImageItemChange],
  );

  const handleImageClear = useCallback(() => {
    setSelectedImage(undefined);
    onImageItemChange?.(undefined);
    form.setFieldValue(Field.IMAGE, undefined);
  }, [form, onImageItemChange]);

  const handleOk = useCallback(async () => {
    try {
      await form.validate();
    } catch {
      return false;
    }
    if (!selectedImage?.url && !initDisk?.image?.url) {
      Message.error(t('common:image.select'));
      return false;
    }
    form.setFieldValue(Field.IMAGE, selectedImage ?? initDisk?.image);
    await onOk();
  }, [form, initDisk?.image, onOk, selectedImage, t]);

  const imageSummarySource = useMemo(() => {
    const image = watchedImage ?? initDisk?.image;
    const displayName = image?.name || watchedImageId;
    if (!displayName && !watchedImageId) return null;
    return {
      displayName: displayName || watchedImageId,
      os: image?.os,
      version: image?.version,
      architecture: image?.architecture,
      bootloader: image?.bootloader,
    };
  }, [initDisk?.image, watchedImage, watchedImageId]);

  const modalTitle = title ?? (initDisk ? t('components:edit.system.disk') : t('components:create.system.disk'));

  return (
    <PluginModal className={styles.createModal} visible={visible} title={modalTitle} focusLock={false} onCancel={onCancel} onOk={handleOk}>
      <div className={styles.modalBody}>
        <Form form={form} layout="vertical">
          {/* 段 1：镜像选择（对齐 CI ImageBlock 职责，数据仍写入 systemDisk.image） */}
          <ImageResourceSelectField
            form={form}
            queryObj={imageQuery}
            imageKeyField={Field.IMAGE_ID}
            imageUrlField={Field.IMAGE_DISPLAY}
            summarySource={imageSummarySource}
            readOnly={selectionReadOnly}
            onImageChange={handleImageSelected}
            onImageClear={handleImageClear}
          />
          {/* 段 2：盘配置（回收 / 存储类 / 容量 / 计价；CI 对应独立 system-disk-size） */}
          <DiskVolumeFields
            form={form}
            diskSizeField={Field.DISK_SIZE}
            storageClassField={Field.STORAGE_CLASS}
            reclaimPolicyField={Field.RECLAIM_POLICY}
            enabled={visible}
            billModelId={billModelId}
            deploymentTypeId={deploymentTypeId}
            paymentTiming={paymentTiming}
            skuType={skuType}
            resourceEditMode={resourceEditMode}
            resourceCode={resourceCode}
            selectionReadOnly={selectionReadOnly}
            getPopupContainer={getModalPopupContainer}
            diskSizeInitialValue={DEFAULT_DISK_SIZE}
            markCount={DISK_SIZE_MARK_COUNT}
          />
          <OptionMetaRootField />
          <Form.Item field={Field.IMAGE} hidden>
            <Input />
          </Form.Item>
        </Form>
      </div>
    </PluginModal>
  );
}

export function getImageVmCreateModalValues(
  form: ImageVmCreateModalProps['form'],
  imageItem?: CommonImageItemType,
  options?: { resourceEditMode?: string },
) {
  const values = form.getFieldsValue();
  const image = values[Field.IMAGE] as SystemDiskImageInfo | undefined;
  const imageDisplayName = values[Field.IMAGE_DISPLAY] as string | undefined;
  const pricing = normalizeVendorDeviceMeta(readDiskPricingMeta(values as Record<string, unknown>));
  const diskVolume = readDiskVolumeFormValues(
    values as Record<string, unknown>,
    {
      diskSize: Field.DISK_SIZE,
      storageClass: Field.STORAGE_CLASS,
      reclaimPolicy: Field.RECLAIM_POLICY,
    },
    { lockReclaimPolicy: isReclaimPolicyLocked(options?.resourceEditMode) },
  );
  return {
    ...diskVolume,
    imageId: values[Field.IMAGE_ID] as string | undefined,
    pricing,
    image: image
      ? {
          ...image,
          name: image.name || imageDisplayName || imageItem?.name,
        }
      : undefined,
    imageItem,
    diskName: SYSTEM_DISK_NAME,
  };
}
